"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, Gift, CircleCheck, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function RegistrationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-green-500/10 border border-green-500/20 rounded-xl">
        <div className="size-16 rounded-lg bg-green-500/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-green-500" />
        </div>
        <h3 className="text-2xl font-bold text-green-500">Înregistrare Reușită!</h3>
        <p className="text-green-400">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-green-500/50 text-green-500 hover:bg-green-500/10">
          Înregistrează pe altcineva
        </Button>
      </div>;
  }
  const inputClasses = "flex h-12 w-full rounded-md border border-input bg-background/50 px-4 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
  const activityOptions = ["Antreprenor / dețin o afacere", "Freelancer / prestator servicii", "Specialist într-un domeniu (beauty, medicină, servicii)", "Angajat care vrea să învețe marketing", "Creator de conținut", "Alt domeniu"];
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="full_name" rules={{
          required: "Numele este obligatoriu"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Nume *</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ana Popescu" className={inputClasses} />
                </FormControl>
                <FormMessage className="text-destructive" />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Numărul de telefon este obligatoriu"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Telefon *</FormLabel>
                <FormControl>
                  <Input {...field} type="tel" placeholder="+40 712 345 678" className={inputClasses} />
                </FormControl>
                <FormMessage className="text-destructive" />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="email" rules={{
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Adresă de email invalidă"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Email</FormLabel>
                <FormControl>
                  <Input {...field} type="email" placeholder="ana@example.com" className={inputClasses} />
                </FormControl>
                <FormMessage className="text-destructive" />
              </FormItem>} />

          <FormField control={control} name="activity_type" rules={{
          required: "Te rugăm să selectezi tipul de activitate"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Ce tip de activitate ai? *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className={cn(inputClasses, "w-full justify-between")}>
                      <SelectValue placeholder="Selectează o opțiune" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {activityOptions.map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                        {option}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage className="text-destructive" />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-destructive text-sm font-medium">{submitError}</p>
          </div>}

        <div className="pt-4 flex justify-center">
          <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto min-w-[280px] h-16 text-lg font-bold rounded-lg bg-white text-black hover:bg-white/90 shadow-xl transition-all duration-300 hover:scale-105 border-none">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-6" />
                Se procesează...
              </> : 'ÎNREGISTREAZĂ-TE ACUM!'}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function BonusOpportunity() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="oportunitate-bonus" className="dark bg-background text-foreground py-24 px-4 relative overflow-hidden min-h-screen flex flex-col items-center justify-center">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-background via-background to-transparent pointer-events-none" />

      <div className="max-w-5xl w-full mx-auto relative z-10 space-y-16">
        <div ref={headerRef} className={`transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Card className="relative overflow-hidden border-border/40 bg-card/60 backdrop-blur-xl shadow-2xl shadow-primary/10 group">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,var(--color-primary)_0%,transparent_70%)] opacity-10 group-hover:opacity-20 transition-opacity duration-700 pointer-events-none" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDUiLz4KPC9zdmc+')] opacity-20 mix-blend-overlay pointer-events-none" />
            
            <CardContent className="p-12 md:p-20 flex flex-col items-center text-center relative z-10">
              <div className="mb-8 relative">
                <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-lg" />
                <Gift className="size-20 text-primary relative z-10 drop-shadow-[0_0_15px_var(--color-primary)]" />
              </div>
              
              <div className="flex items-center gap-2 mb-4 text-primary font-semibold tracking-wider uppercase text-sm md:text-base">
                <Sparkles className="size-4" />
                <span>Bonus</span>
                <Sparkles className="size-4" />
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-b from-primary to-chart-2 leading-tight drop-shadow-sm max-w-3xl mx-auto">
                Participanții la curs primesc și:<br />
              </h2>
              
              <div className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto space-y-2">
                <p> Structuri de reels</p>
                <p> Strategii de reclame</p>
                <p> Acces într-o comunitate de antreprenori și specialiști</p>
                <p>(acces later în MAIN Club)</p>
              </div>
            </CardContent>
            
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
          </Card>
        </div>

        <div ref={formRef} className={`transition-all duration-1000 delay-300 transform ${isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="bg-card/40 backdrop-blur-md border border-border/50 rounded-2xl p-8 md:p-12 shadow-xl">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-bold text-foreground mb-3">Rezervă locul acum!</h3>
              <p className="text-muted-foreground">Dacă vrei să înțelegi cum funcționează marketingul în realitate și să atragi clienți pentru afacerea ta</p>
            </div>

            <FormProvider formId="bonus_opportunity_form_home" sectionName="BonusOpportunity" defaultValues={{
            full_name: "",
            phone: "",
            email: "",
            activity_type: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            phone: {
              required: true
            },
            email: {
              required: false,
              pattern: "email"
            },
            activity_type: {
              required: true
            }
          }} submitText="ÎNREGISTREAZĂ-TE ACUM!" successMessage="Mulțumim pentru înregistrare! Locul tău este rezervat." errorMessage="A apărut o eroare la trimiterea înregistrării. Te rugăm să încerci din nou.">
              <RegistrationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}